import { useState, useMemo } from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import * as MuiIcons from '@mui/icons-material';
import { useAmenities } from '../../config/hooks/useAmenities';

interface AmenitiesGridProps {
  amenities: string[];
  maxVisible?: number;
}

// Helper function to convert snake_case icon name to PascalCase for MUI icon lookup
const toPascalCase = (str: string): string => {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
};

// Label formatting for amenities
const formatAmenityLabel = (amenity: string): string => {
  // Handle snake_case
  if (amenity.includes('_')) {
    return amenity
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Handle camelCase
  const formatted = amenity.replace(/([A-Z])/g, ' $1').trim();
  // Capitalize first letter of each word
  return formatted
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const GridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 12,
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

const AmenityItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  backgroundColor: alpha(theme.palette.success.main, 0.04),
  borderRadius: 10,
  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.main, 0.08),
    transform: 'translateY(-1px)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.success.main,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  '& .MuiSvgIcon-root': {
    fontSize: 20,
  },
}));

const AmenityLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  flex: 1,
}));

const ShowMoreButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  padding: '12px 16px',
  marginTop: 12,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 10,
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.main, 0.05),
    borderColor: theme.palette.success.main,
    color: theme.palette.success.main,
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: 24,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
}));

export function AmenitiesGrid({ amenities, maxVisible = 8 }: AmenitiesGridProps) {
  const [showAll, setShowAll] = useState(false);
  const { data: amenityConfigs } = useAmenities();

  // Build dynamic icon map from amenityConfigs
  const iconMap = useMemo(() => {
    const map: Record<string, React.ReactNode> = {};
    amenityConfigs?.forEach((config) => {
      if (config.icon) {
        // Convert icon name to PascalCase to match MUI icon component names
        const iconName = toPascalCase(config.icon);
        // Try to get the icon component from MUI Icons
        const IconComponent = (MuiIcons as any)[iconName];
        if (IconComponent) {
          map[config.code] = <IconComponent />;
        }
      }
    });
    return map;
  }, [amenityConfigs]);

  // Build name map from amenityConfigs
  const nameMap = useMemo(() => {
    const map: Record<string, string> = {};
    amenityConfigs?.forEach((config) => {
      map[config.code] = config.name;
    });
    return map;
  }, [amenityConfigs]);

  // Get icon for amenity code with fallback
  const getAmenityIcon = (code: string): React.ReactNode => {
    return iconMap[code] || <CheckCircleIcon />;
  };

  // Get name for amenity code with fallback
  const getAmenityName = (code: string): string => {
    return nameMap[code] || formatAmenityLabel(code);
  };

  if (!amenities || amenities.length === 0) {
    return <EmptyState>No amenities listed for this venue.</EmptyState>;
  }

  const visibleAmenities = showAll ? amenities : amenities.slice(0, maxVisible);
  const hiddenCount = amenities.length - maxVisible;
  const hasMoreToShow = amenities.length > maxVisible;

  return (
    <Box>
      <GridContainer>
        {visibleAmenities.map((amenity, index) => (
          <AmenityItem key={index}>
            <IconWrapper>{getAmenityIcon(amenity)}</IconWrapper>
            <AmenityLabel>{getAmenityName(amenity)}</AmenityLabel>
          </AmenityItem>
        ))}
      </GridContainer>

      {hasMoreToShow && (
        <ShowMoreButton onClick={() => setShowAll(!showAll)}>
          {showAll ? (
            <>
              <ExpandLessIcon sx={{ fontSize: 20 }} />
              Show less
            </>
          ) : (
            <>
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
              Show {hiddenCount} more amenities
            </>
          )}
        </ShowMoreButton>
      )}
    </Box>
  );
}
